import { Injectable } from "@angular/core";
import { StateService } from "./state.service";

@Injectable({ providedIn: "root" })
export class PublishedGroupingService {
  constructor(private stateService: StateService) {}

  normalizeStatusApiValue(value: any): any {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    if (typeof value !== "string") {
      return value;
    }

    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_")
      .replace(/_+/g, "_");
  }

  getGroupedFilterField(dimension: string | null): string | null {
    if (!dimension) {
      return null;
    }

    const fieldMap: Record<string, string> = {
      country: "countries",
      region: "regions",
      status: "plan_status",
      ka: "key_account_ids",
      key_account: "key_account_ids",
      key_account_id: "key_account_ids",
      created: "created",
      tag: "tag"
    };

    return fieldMap[dimension] ?? dimension;
  }

  isKeyAccountDimension(dimension: string | null): boolean {
    return ["ka", "key_account", "key_account_id"].includes(
      String(dimension).toLowerCase()
    );
  }

  resolveGroupedFilterValue(
    dimension: string | null,
    value: any,
    keyAccountList: any[] = []
  ): any {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    const normalizedDimension = (dimension || "").toLowerCase();
    const isKeyAccountDimension = this.isKeyAccountDimension(dimension);
    const isStatusDimension =
      normalizedDimension === "status" ||
      normalizedDimension === "experiment_status" ||
      normalizedDimension === "experiment_statuses";

    if (isStatusDimension) {
      return this.normalizeStatusApiValue(value);
    }

    if (!isKeyAccountDimension) {
      return value;
    }

    const match = keyAccountList?.find((item: any) => {
      const idMatches = item?.id === value;
      const nameMatches = item?.name === value;
      const labelMatches = item?.label === value;
      const keyNameMatches = item?.key_account === value;
      return idMatches || nameMatches || labelMatches || keyNameMatches;
    });

    return match?.id ?? value;
  }

  getNodeGroupKey(node: any): string {
    return node?.id || node?.key || node?.label || node?.value || "node";
  }

  syncGroupedNodePayload(
    targetNode: any,
    payload: {
      experiments?: any[];
      page?: number;
      page_size?: number;
      total?: number;
      total_pages?: number;
    }
  ): void {
    if (!targetNode) {
      return;
    }

    targetNode.experiments = payload.experiments;
    targetNode.page = payload.page;
    targetNode.page_size = payload.page_size;
    targetNode.total = payload.total;
    targetNode.total_pages = payload.total_pages;
  }

  findGroupedPublishedNode(nodes: any[], targetKey: string): any | null {
    for (const node of nodes || []) {
      const nodeKey = this.getNodeGroupKey(node);
      const dataKey = this.getNodeGroupKey(node?.data || {});

      if (
        nodeKey === targetKey ||
        dataKey === targetKey ||
        node?.id === targetKey
      ) {
        return node;
      }

      const childMatch = this.findGroupedPublishedNode(
        node?.children || [],
        targetKey
      );
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  findGroupedPublishedPath(
    nodes: any[],
    targetKey: string,
    currentPath: any[] = []
  ): any[] | null {
    for (const node of nodes || []) {
      const nextPath = [...currentPath, node];
      const nodeKey = this.getNodeGroupKey(node);
      const dataKey = this.getNodeGroupKey(node?.data || {});

      if (
        nodeKey === targetKey ||
        dataKey === targetKey ||
        node?.id === targetKey
      ) {
        return nextPath;
      }

      const childMatch = this.findGroupedPublishedPath(
        node?.children || [],
        targetKey,
        nextPath
      );
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  private buildGroupedNodeKey(
    group: any,
    groupTag: string | null,
    parentKey: string | null = null
  ): string {
    const rawKey = group?.id ?? group?.value ?? group?.label ?? "group";

    return [parentKey, groupTag, rawKey]
      .filter((part) => part !== null && part !== undefined && part !== "")
      .map((part) => String(part).trim().toLowerCase().replace(/\s+/g, "_"))
      .join("_");
  }

  normalizeGroupedData(
    data: any,
    groupByLevel: number = 1,
    parentKey: string | null = null
  ): any {
    if (!data || !Array.isArray(data.groups)) {
      return [];
    }

    const groupTag: string = data.group_by[groupByLevel - 1];

    data.groups = data.groups.map((group: any) => ({
      ...group,
      value: group?.value ?? group?.label ?? null,
      group_by_level: groupByLevel,
      key: this.buildGroupedNodeKey(group, groupTag, parentKey),
      expanded: false,
      is_parent: true,
      group_tag: groupTag || null
    }));

    return data;
  }

  mergeGroupedPublishedNode(
    targetNode: any,
    incomingGroups: any,
    groupByLevel: number
  ): void {
    if (!targetNode) {
      return;
    }

    targetNode["children"] = this.normalizeGroupedData(
      incomingGroups,
      groupByLevel,
      this.getNodeGroupKey(targetNode)
    ).groups;
    targetNode["page"] = incomingGroups.page;
    targetNode["page_size"] = incomingGroups.page_size;
    targetNode["total"] = incomingGroups.total;
    targetNode["total_pages"] = incomingGroups.total_pages;
  }

  transformExperimentItems(experiments: any[]): any[] {
    const currentUser = this.stateService.getUser();

    return experiments.map((item: any) => ({
      ...item,
      status: item.status === "cloning" ? "completed" : item.status || null,
      isEditable: false,
      isLoading: false,
      hasShared: item.user !== currentUser?.id,
      statusOptions: null,
      access_type: "read"
    }));
  }
}
